/* global React */
const { useEffect, useState } = React;

// ---------- HERO ----------
function Hero() {
  return (
    <section className="hero hero--statement" aria-labelledby="hero-title">
      <div className="hero__label">
        <span className="dot" />
        <span className="mono">Fractional CTO · Advisory · Development</span>
      </div>
      <div className="grid-12">
        <h1 id="hero-title">
          Experienced hands<br />
          for the <em>foundational decisions</em><br />
          that shape your tech product
        </h1>
        <div className="hero__meta">
          <div className="mono hero__meta-label">Principal</div>
          <div className="hero__meta-name">Dan Paull</div>
          <div className="mono hero__meta-loc">Est. practice · Remote / AU</div>
        </div>
        <div className="hero__intro">
          <p>
            I'm Dan. For over 25 years I've built software, led engineering
            teams, and learned what drives good technical decisions.
          </p>
          <p>
            PDM Advisory is how I help founders and technical leaders now:
            honest advice, hands-on delivery, and the operational habits that
            carry a team from concept to delivery.
          </p>
        </div>
      </div>
    </section>
  );
}

// ---------- SERVICES ----------
const SERVICES = [
  {
    key: "cto",
    title: "Fractional CTO",
    blurb:
      "A part-time technical partner while you're finding your feet — cementing good practices and setting you up for permanent technical leadership.",
    points: [
      "Technical strategy and roadmap",
      "Hiring, levelling, and team building",
      "Architecture and platform decisions",
      "Board and investor technical diligence",
    ],
    engagement: "3–6 months · 1–2 days/wk",
  },
  {
    key: "advisory",
    title: "Technical Advisory",
    blurb:
      "Short, focused engagements. A second pair of eyes, a due-diligence review, or an outside opinion on a decision that can't be wrong.",
    points: [
      "Architecture and code reviews",
      "Build-vs-buy and vendor selection",
      "Acquisition and investment diligence",
      "Org and process assessments",
    ],
    engagement: "1–6 weeks · scoped",
  },
  {
    key: "software",
    title: "Software Development",
    blurb:
      "Hands-on build. Prototypes, hard-to-hire-for projects, and the load-bearing pieces that need to hold up.",
    points: [
      "Zero-to-one prototypes and MVPs",
      "Technical spikes and de-risking",
      "R&D for novel systems",
      "Greenfield systems design",
    ],
    engagement: "Project-based",
  },
];

function Services() {
  return (
    <section id="services" className="section" aria-labelledby="services-title">
      <div className="container">
        <header className="section__head">
          <div className="section__num mono">§ 01 — Services</div>
          <h2 id="services-title" className="section__title">
            Ways we can work <em>together</em>
          </h2>
        </header>
        <div className="services--stacked">
          {SERVICES.map((s, i) => (
            <article className="svc" key={s.key}>
              <div className="svc__head">
                <div className="svc__num">
                  <span className="n">
                    S/{String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="svc__title">
                  <h3>{s.title}</h3>
                </div>
              </div>
              <div className="svc__body">
                <p>{s.blurb}</p>
                <p className="muted">
                  Typical engagement: {s.engagement.toLowerCase()}. Scope
                  decided together, not borrowed from a template.
                </p>
                <ol className="svc__points">
                  {s.points.map((p, j) => (
                    <li key={p}>
                      <span className="k">{String(j + 1).padStart(2, "0")}</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- PRINCIPLES ----------
const PRINCIPLES = [
  {
    t: "Culture is king",
    d: "Psychological safety and real autonomy produce teams that do meaningful work. Innovation, retention, productivity and quality all follow.",
    points: [
      "Psychological safety as a health metric",
      "Autonomy paired with high accountability",
      "Continuous professional development, for everyone",
      "Retention through positive engagement",
    ],
  },
  {
    t: "Just get moving",
    d: "Scope from the top, build from the bottom — the foundations don't change much while the plans above them always will. Motion reveals what planning can't.",
    points: [
      "Top-down scope, bottom-up build",
      "Contingencies and stage gates as pressure relief",
      "Refactor early; minimise blast radius",
      "Plan for change, not certainty"
    ],
  },
  {
    t: "Pragmatism over ideology",
    d: "The best answer is rarely the one in a book — it's the set of compromises that fits your resources, your team, and this particular moment. Ideology is expensive.",
    points: [
      "Use the right tool for the job",
      "Leverage your technical debt",
      "Trade-offs chosen openly",
      "Good enough is enough",
    ],
  },
];

function Principles() {
  return (
    <section
      id="principles"
      className="section"
      aria-labelledby="principles-title"
    >
      <div className="container">
        <header className="section__head">
          <div className="section__num mono">§ 02 — Principles</div>
          <h2 id="principles-title" className="section__title">
            Operating principles that <em>drive good outcomes</em>
          </h2>
        </header>
        <div className="principles__list">
          {PRINCIPLES.map((p, i) => (
            <article className="principles__item" key={p.t}>
              <div className="principles__head">
                <div className="n">P/{String(i + 1).padStart(2, "0")}</div>
                <h3>{p.t}</h3>
              </div>
              <div className="principles__body">
                <p>{p.d}</p>
                <ol className="principles__points">
                  {p.points.map((pt, j) => (
                    <li key={pt}>
                      <span className="k">{String(j + 1).padStart(2, "0")}</span>
                      <span>{pt}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- CONTACT ----------
function Contact() {
  return (
    <section id="contact" className="section contact" aria-labelledby="contact-title">
      <div className="container">
        <header className="section__head">
          <div className="section__num mono">§ 03 — Contact</div>
          <h2 id="contact-title" className="section__title">
            If I can help, <em>let's talk</em>
          </h2>
        </header>
        <div className="contact__grid">
          <div className="contact__email">
            <a href="mailto:dan@pdmadvisory.au">dan@pdmadvisory.au</a>
            <p className="contact__note">
              A short message about what you're working on and where I
              might help is enough to get us moving. I know time is often
              of the essence, and I'll reply as soon as I can.
            </p>
          </div>
          <div className="contact__meta">
            <div className="cell">
              <div className="k">Based</div>
              <div className="v">Perth, AWST, UTC+8</div>
            </div>
            <div className="cell">
              <div className="k">Availability</div>
              <div className="v">Limited, but some capacity currently</div>
            </div>
            <div className="cell">
              <div className="k">Engagement</div>
              <div className="v">Fractional · Advisory · Project</div>
            </div>
            <div className="cell">
              <div className="k">Focus</div>
              <div className="v">Seed to Series A</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- TOPBAR / FOOTER ----------
const NAV = [
  { href: "#services", label: "Services", id: "services" },
  { href: "#principles", label: "Principles", id: "principles" },
  { href: "#contact", label: "Contact", id: "contact" },
];

function TopBar({ active }) {
  return (
    <header className="topbar">
      <div className="container topbar__inner">
        <a className="brand" href="#top">
          <span className="brand__mark">
            <img src="assets/pdm-logo.svg" alt="PDM Advisory" />
          </span>
          <span className="brand__text">
            <span className="brand__name">PDM Advisory</span>
            <span className="brand__slash"> / </span>
            <span className="brand__person">Dan Paull</span>
          </span>
        </a>
        <nav aria-label="Primary">
          {NAV.map((n) => (
            <a
              key={n.id}
              href={n.href}
              aria-current={active === n.id ? "true" : undefined}
            >
              {n.label}
            </a>
          ))}
        </nav>
        <a href="#contact" className="topbar__cta">
          Get in touch →
        </a>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="foot">
      <div className="container foot__inner">
        <div className="foot__legal">
          <div>© Performance Does Matter, trading as PDM Advisory</div>
          <div>ABN 41 330 534 911</div>
        </div>
        <div>
          <a
            href="https://www.linkedin.com/in/danielpaull"
            target="_blank"
            rel="noreferrer noopener"
          >
            LinkedIn
          </a>
        </div>
      </div>
    </footer>
  );
}

Object.assign(window, {
  Hero,
  Services,
  Principles,
  Contact,
  TopBar,
  Footer,
});
